import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BackgroundJob,
  BackgroundJobScopeEnum,
  BackgroundJobsQuery,
  BackgroundJobStatusEnum,
  SortDirectionEnum,
  useBackgroundJobDismissMutation,
  useBackgroundJobProgressSubscription,
  useBackgroundJobsQuery,
} from 'graphql-common';
import { useTabActivity } from '@lib/hooks/useTabActivity';
import { EventBusEvents, jobEventBus } from '@lib/utils/eventBus';
import { logger } from '@lib/services/logger';
import handleJobCompletion from 'components/JobTracker/utils/handleJobCompletion';
import { SEED_JOBS_LIMIT } from 'components/JobTracker/constants';

const isRunningStatus = (status: BackgroundJobStatusEnum) =>
  status === BackgroundJobStatusEnum.Queued ||
  status === BackgroundJobStatusEnum.Working ||
  status === BackgroundJobStatusEnum.Retrying;

// Upsert a job node into the id-keyed map. Child frames (parentId set) are
// merged into their root's `children`; root nodes win by `updatedAt` so a stale
// reconcile query never clobbers a fresher live frame.
export const upsertJob = (
  map: Map<string, BackgroundJob>,
  node: BackgroundJob,
): Map<string, BackgroundJob> => {
  const next = new Map(map);
  if (node.parentId && node.rootId && node.rootId !== node.id) {
    const root = next.get(node.rootId);
    if (root) {
      // `treeStatus` is computed off the shared rootId, so it's identical on
      // every node of the tree — a child's own frame carries a value just as
      // fresh as the root's, even though the BE only re-broadcasts whichever
      // node itself transitioned (never the parent). Mirror it onto the
      // cached root so its card reflects the child's outcome without waiting
      // for the root to transition again.
      // Replace the matching child, or append it when it isn't cached yet: a
      // child can spawn after its root is already in the map (e.g. a document
      // field-detection child created once the import root completes), and the
      // BE re-broadcasts only the transitioned node, never the parent — so a
      // plain `.map` (replace-only) would silently drop that late child's row.
      const children = root.children || [];
      const hasChild = children.some((child) => child.id === node.id);
      next.set(node.rootId, {
        ...root,
        treeStatus: node.treeStatus,
        children: hasChild
          ? children.map((child) => (child.id === node.id ? node : child))
          : [...children, node],
      });
    }
    return next;
  }
  const existing = next.get(node.id);
  if (
    existing?.updatedAt &&
    node.updatedAt &&
    node.updatedAt < existing.updatedAt
  ) {
    return next;
  }
  next.set(node.id, node);
  return next;
};

export default function useBackgroundJobs() {
  const [jobsMap, setJobsMap] = useState<Map<string, BackgroundJob>>(new Map());
  // Previous status per job id, to fire side-effects only on transition.
  const prevStatusRef = useRef<Map<string, BackgroundJobStatusEnum>>(new Map());
  // Subscribe first, seed second (BE background-jobs guide §1). This
  // subscription replays nothing on subscribe, so any frame that lands between
  // the seed response and the subscribe request is lost outright — a job that
  // finishes in that window keeps its stale row until the next tab-focus
  // reconcile. Flipped in an effect below, which runs after useSubscription's
  // own effect has sent the subscribe.
  const [subscribed, setSubscribed] = useState(false);

  const { data, error, loading, refetch } = useBackgroundJobsQuery({
    skip: !subscribed,
    variables: {
      scope: BackgroundJobScopeEnum.All,
      filters: [{ dismissed: { predicate: { false: true } } }],
      sorts: { createdAt: SortDirectionEnum.Desc },
      limit: SEED_JOBS_LIMIT,
    },
    fetchPolicy: 'cache-and-network',
  });

  // Derived state from `data` (React 19 pattern) replaces the deprecated
  // `onCompleted` option. The ref starts at `undefined` (not the current
  // collection) so a synchronously-cached first response — same reference on
  // mount as any later one — still triggers the initial merge.
  const collectionRef = useRef<
    BackgroundJobsQuery['data']['collection'] | undefined
  >(undefined);
  if (collectionRef.current !== data?.data.collection) {
    collectionRef.current = data?.data.collection;
    const collection = (data?.data.collection || []) as BackgroundJob[];
    if (collection.length) {
      setJobsMap((prev) => {
        let next = prev;
        collection.forEach((node) => {
          next = upsertJob(next, node);
        });
        return next;
      });
    }
  }

  useEffect(() => {
    if (error) {
      logger.debug('JobTracker: seed query error', { error });
    }
  }, [error]);

  useBackgroundJobProgressSubscription({
    fetchPolicy: 'no-cache',
    onData: ({ data: subscriptionData }) => {
      const node = subscriptionData.data?.backgroundJobProgress?.node;
      if (!node) return;
      setJobsMap((prev) => upsertJob(prev, node as BackgroundJob));
    },
    onError: (subscriptionError) => {
      logger.debug('JobTracker: subscription error', {
        error: subscriptionError,
      });
    },
  });

  // Releases the seed query, one effect after the subscription hook above has
  // sent its subscribe. Ordering the two is all the client can do — the cable
  // gives no "subscribed" acknowledgement to wait on — but it closes the window
  // where the seed and the subscribe raced each other.
  useEffect(() => {
    setSubscribed(true);
  }, []);

  // Immediate seed when a trigger (import / QR / report) starts a job, so the
  // pop-up appears without waiting for the first subscription frame.
  useEffect(() => {
    const handler = (node: BackgroundJob) => {
      setJobsMap((prev) => upsertJob(prev, node));
    };
    jobEventBus.on(EventBusEvents.JobCreated, handler);
    return () => {
      jobEventBus.off(EventBusEvents.JobCreated, handler);
    };
  }, []);

  // Reconcile durable counters after the tab regains focus (covers cable drops).
  useTabActivity({
    onFocus: () => {
      refetch();
    },
  });

  const jobs = Array.from(jobsMap.values())
    .filter((job) => !job.dismissed)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));

  // Auto-download finished result-bearing jobs (once) and notify consumers when
  // a job completes, firing only on the status transition.
  useEffect(() => {
    const prevStatuses = prevStatusRef.current;
    jobs.forEach((job) => {
      const prevStatus = prevStatuses.get(job.id);
      if (prevStatus !== job.status) {
        if (job.status === BackgroundJobStatusEnum.Complete) {
          handleJobCompletion(job);
          if (job.kind) {
            jobEventBus.emit(EventBusEvents.JobCompleted, {
              id: job.id,
              kind: job.kind,
            });
          }
        }
        prevStatuses.set(job.id, job.status);
      }
    });
  }, [jobs]);

  const [dismissMutation] = useBackgroundJobDismissMutation();

  const removeJobFromList = useCallback((id: string) => {
    setJobsMap((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    prevStatusRef.current.delete(id);
  }, []);

  const dismissAll = useCallback(() => {
    jobs.forEach((job) => {
      if (!isRunningStatus(job.status) && job.canDismiss?.value) {
        dismissMutation({ variables: { input: { id: job.id } } });
        removeJobFromList(job.id);
      }
    });
  }, [jobs, dismissMutation, removeJobFromList]);

  return {
    jobs,
    loading,
    refetch,
    removeJobFromList,
    dismissAll,
  };
}
